// Shared constants for Budget components
// This file centralizes all dropdown options to avoid duplication

export const STATE_OPTIONS = [
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
  { label: 'Draft', value: 'draft' },
  { label: 'Completed', value: 'completed' },
];

// Budget Group Setup Flow.
// 'advanced' is the legacy flow (kept as the stored value so the existing form logic is unchanged);
// 'unified' is the new flow (UAP). Quick Setup was removed - legacy 'quick' values are treated as 'advanced'.
export const SETUP_FLOW_ADVANCED = { label: 'Advanced Legacy', value: 'advanced' };
export const SETUP_FLOW_UNIFIED = { label: 'Unified', value: 'unified' };

export const SETUP_FLOW_OPTIONS = [
  SETUP_FLOW_ADVANCED,
  SETUP_FLOW_UNIFIED,
];

export const AGENCY_INTERNAL_OPTIONS = [
  { label: 'Agency View', value: false },
  { label: 'Limitless View', value: true },
];

export const BUDGET_DRIVER_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Monthly', value: 'monthly' },
];

export const AD_TYPE_CSV_OPTIONS = [
  { label: 'Sponsored Product and Sponsored Display', value: 'SP,SD' },
  { label: 'Sponsored Brand', value: 'SB' },
  { label: 'Sponsored Tv', value: 'ST' },
];

// Walmart ad types - any combination can be selected (stored as ad_type_csv)
export const WALMART_AD_TYPE_OPTIONS = [
  { label: 'Sponsored Products (SP)', value: 'SP' },
  { label: 'Sponsored Display (SD)', value: 'SD' },
  { label: 'Sponsored Brands (SB)', value: 'SB' },
  { label: 'Sponsored Television (ST)', value: 'ST' },
];

// Amazon Unified ad types: full AD Type name, upper case, with spaces. Only Sponsored Products
// is active (100%); the rest stay at 0. `existingId` keeps the saved record id when editing.
export const UNIFIED_AMAZON_AD_TYPES = {
  SP: 'SPONSORED PRODUCTS',
  SD: 'SPONSORED DISPLAY',
  SB: 'SPONSORED BRANDS',
  ST: 'SPONSORED TELEVISION',
};

export const buildUnifiedAmazonBudgetGroupRecords = (existingId = 0) => [
  { budget_group_daily_id: existingId, ad_type: UNIFIED_AMAZON_AD_TYPES.SP, allocation_percent: 100 },
  { budget_group_daily_id: 0, ad_type: UNIFIED_AMAZON_AD_TYPES.SD, allocation_percent: 0 },
  { budget_group_daily_id: 0, ad_type: UNIFIED_AMAZON_AD_TYPES.SB, allocation_percent: 0 },
  { budget_group_daily_id: 0, ad_type: UNIFIED_AMAZON_AD_TYPES.ST, allocation_percent: 0 },
];

// AD Type groups for budget allocation - order matters!
export const AD_TYPE_GROUPS = [
  { label: 'SP + SD', value: 'SP,SD', key: 'sp_sd' },
  { label: 'SB', value: 'SB', key: 'sb' },
  { label: 'ST', value: 'ST', key: 'st' },
];

export const LIMITLESS_BUDGET_OPTIONS = [
  { label: 'Strict Budget', value: 0 },
  { label: 'No Budget', value: 1 },
  { label: 'Flexible Budget', value: 2 },
];

export const INCLUDES_OPTIONS = [
  { label: 'All ADs', value: '' },
  { label: 'Limitless ADs', value: 'limitless' },
  { label: 'Manual ADs', value: 'external' },
];

export const SPEND_PRIORITY_OPTIONS = [
  { label: 'Most Brand', value: '-2' },
  { label: 'More Brand', value: '-1' },
  { label: 'Brand and Non-Brand', value: '0' },
  { label: 'More NonBrand', value: '1' },
  { label: 'Most NonBrand', value: '2' },
];

export const SPEND_TACTIC_OPTIONS = [
  { label: 'Max Performance', value: 'max_perf' },
  { label: 'Full Budget Spend', value: 'full_spend' },
];

// Google-specific Spend Tactic (always use this for Google budgets)
export const GOOGLE_SPEND_TACTIC_OPTIONS = [
  { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' },
];

// Target-specific Spend Tactic (always use this for Target budgets)
export const TARGET_SPEND_TACTIC_OPTIONS = [
  { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' },
];

export const SPEND_SPEED_OPTIONS = [
  { label: 'Standard', value: 'speed_level_1' },
  { label: 'Fast', value: 'speed_level_2' },
  { label: 'Fastest', value: 'speed_level_3' },
];

export const COST_TARGET_TYPE_OPTIONS = [
  { label: 'Blended', value: 'blended' },
  { label: 'Brand/NonBrand', value: 'bnb' },
];

export const STATUS_OPTIONS = [
  { label: 'ON', value: 'ON' },
  { label: 'OFF', value: 'OFF' },
];

export const ACTIVE_DAY_OPTIONS = [
  { label: 'Monday', name: 'monday', value: '0' },
  { label: 'Tuesday', name: 'tuesday', value: '1' },
  { label: 'Wednesday', name: 'wednesday', value: '2' },
  { label: 'Thursday', name: 'thursday', value: '3' },
  { label: 'Friday', name: 'friday', value: '4' },
  { label: 'Saturday', name: 'saturday', value: '5' },
  { label: 'Sunday', name: 'sunday', value: '6' },
];

// Budget Type Options (Item, Brand, DSP, Other)
export const BUDGET_TYPE_OPTIONS = [
  { label: 'Item', value: 'item' },
  { label: 'Brand', value: 'brand' },
  { label: 'DSP', value: 'dsp' },
  { label: 'Other', value: 'other' },
];

// Item Type Options (for Item and Walmart budgets)
export const ITEM_TYPE_OPTIONS = [
  { label: 'SKU', value: 'SKU' },
  { label: 'ASIN', value: 'ASIN' },
  { label: 'ITEM', value: 'ITEM' },
];

export const ITEM_SELECTION_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'Top', value: 'TOP' },
];

export const DESTINATION_OPTIONS = [
  { label: 'On', value: 'ON' },
  { label: 'Off', value: 'OFF' },
];

// Brand-specific options
// NOTE: These map to API sub-types: sbv, sbpc, sbss
// But in rolled-up views, all are displayed as generic "SB"
export const BRAND_AD_FORMAT_OPTIONS = [
  { label: 'Video', value: 'video' }, // Maps to API: sbv
  { label: 'Product Collection', value: 'product_collection' }, // Maps to API: sbpc
  { label: 'Store Spotlight', value: 'store_spotlight' }, // Maps to API: sbss
];

// Mapping of Brand Ad Formats to API sub-types
export const BRAND_AD_FORMAT_TO_API_TYPE = {
  video: 'sbv',
  product_collection: 'sbpc',
  store_spotlight: 'sbss',
};

// Reverse mapping: API sub-types to Brand Ad Formats
export const API_TYPE_TO_BRAND_AD_FORMAT = {
  sbv: 'video',
  sbpc: 'product_collection',
  sbss: 'store_spotlight',
};

// DSP-specific Item Types
export const DSP_ITEM_TYPE_OPTIONS = [
  { label: 'Product Page', value: 'product_page' },
  { label: 'Custom Landing Page', value: 'custom_landing_page' },
  { label: 'Brand Collection', value: 'brand_collection' },
  { label: 'Product Collection', value: 'product_collection' },
  { label: 'Store', value: 'store' },
];

// DSP Spend Tactics (different from Item/Brand)
export const DSP_SPEND_TACTIC_OPTIONS = [
  { label: 'ALL - Full Funnel', value: 'all_full_funnel' },
  { label: 'Top of Funnel', value: 'top_of_funnel' },
  { label: 'Middle of Funnel', value: 'middle_of_funnel' },
  { label: 'Bottom of Funnel', value: 'bottom_of_funnel' },
  { label: 'Loyalty Retargeting Funnel', value: 'loyalty_retargeting_funnel' },
];

// DSP Spend Speed (different from Item/Brand)
export const DSP_SPEND_SPEED_OPTIONS = [
  { label: 'Auto (ALL)', value: 'auto_all' },
  { label: 'AOO', value: 'aoo' },
  { label: 'APD', value: 'apd' },
  { label: '3PE', value: '3pe' },
];

// DSP Gender Targeting
export const DSP_GENDER_OPTIONS = [
  { label: 'Male and Female', value: 'male_and_female' },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

// DSP Ad Format
export const DSP_AD_FORMAT_OPTIONS = [
  { label: 'Display', value: 'display' },
  { label: 'Video', value: 'video' },
];

// ==============================================================================
// PLATFORM-SPECIFIC BUDGET TYPE CONFIGURATIONS
// ==============================================================================
// This configuration drives which fields are shown in the budget group form
// based on the selected platform (retailer) and budget/ad type

export const PLATFORM_BUDGET_TYPES = {
  amazon: {
    label: 'Budget Type',
    options: [
      { label: 'Item', value: 'item', adType: null, itemType: null },
      { label: 'Brand', value: 'brand', adType: null, itemType: null },
      { label: 'DSP', value: 'dsp', adType: null, itemType: null },
      { label: 'Other', value: 'other', adType: null, itemType: null },
    ],
  },
  walmart: {
    label: 'Budget Type',
    options: [
      { label: 'Item', value: 'item', adType: null, itemType: null },
    ],
  },
  target: {
    label: 'Budget Type',
    options: [
      { label: 'Item', value: 'item', adType: 'SPONSORED PRODUCTS', itemType: null },
    ],
  },
  google: {
    label: 'Google Ad Type',
    options: [
      {
        label: 'Search',
        value: 'google_search',
        adType: 'SEARCH',
        itemType: 'URL',
        fields: { url: true, asins: true },
      },
      {
        label: 'Performance Max',
        value: 'google_pmax',
        adType: 'PERFORMANCE MAX',
        itemType: 'URL',
        fields: { url: true },
      },
      {
        label: 'Standard Shopping',
        value: 'google_shop',
        adType: 'STANDARD SHOPPING',
        itemType: 'EXTERNAL_PRODUCT_ID',
        fields: { productIds: true },
      },
    ],
  },
  meta: {
    label: 'Meta Ad Type',
    options: [
      {
        label: 'Facebook + Instagram Feed',
        value: 'meta_facebook_instagram',
        adType: 'FACEBOOK + INSTAGRAM FEED',
        itemType: 'URL',
        fields: { url: true, targeting: true },
      },
      {
        label: 'Facebook Feed',
        value: 'meta_facebook',
        adType: 'FACEBOOK FEED',
        itemType: 'URL',
        fields: { url: true, targeting: true },
      },
      {
        label: 'Instagram Feed',
        value: 'meta_instagram',
        adType: 'INSTAGRAM FEED',
        itemType: 'URL',
        fields: { url: true, targeting: true },
      },
      {
        label: 'Audience Network',
        value: 'meta_audience_network',
        adType: 'META AUDIENCE NETWORK',
        itemType: 'URL',
        fields: { url: true, targeting: true },
      },
    ],
  },
  tiktok: {
    label: 'TikTok Ad Type',
    options: [
      {
        label: 'Smart+ Catalog',
        value: 'tiktok_smart_catalog',
        adType: 'SMART+ CATALOG',
        itemType: 'EXTERNAL_PRODUCT_ID',
        fields: { productIds: true },
      },
      {
        label: 'In-Feed',
        value: 'tiktok_infeed',
        adType: 'IN-FEED',
        itemType: 'URL',
        fields: { url: true },
      },
      {
        label: 'TikTok Shop',
        value: 'tiktok_shop',
        adType: 'TIKTOK SHOP',
        itemType: 'EXTERNAL_PRODUCT_ID',
        fields: { productIds: true },
      },
    ],
  },
};

// ==============================================================================
// GOOGLE-SPECIFIC OPTIONS
// ==============================================================================

export const GOOGLE_SALES_COUNTRY_OPTIONS = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Germany', value: 'DE' },
  { label: 'France', value: 'FR' },
  { label: 'Japan', value: 'JP' },
  { label: 'Australia', value: 'AU' },
  { label: 'Mexico', value: 'MX' },
];

// ==============================================================================
// META-SPECIFIC OPTIONS
// ==============================================================================

export const META_GENDER_OPTIONS = [
  { label: 'All Genders', value: 0 },
  { label: 'Male', value: 1 },
  { label: 'Female', value: 2 },
];

// ==============================================================================
// LANGUAGE OPTIONS (Top 10 most common)
// ==============================================================================

export const LANGUAGE_OPTIONS = [
  { label: 'English (US)', value: 'en_US' },
  { label: 'English (UK)', value: 'en_GB' },
  { label: 'Spanish (Spain)', value: 'es_ES' },
  { label: 'Spanish (Mexico)', value: 'es_MX' },
  { label: 'French (France)', value: 'fr_FR' },
  { label: 'German (Germany)', value: 'de_DE' },
  { label: 'Italian (Italy)', value: 'it_IT' },
  { label: 'Portuguese (Brazil)', value: 'pt_BR' },
  { label: 'Japanese (Japan)', value: 'ja_JP' },
  { label: 'Chinese (Simplified)', value: 'zh_CN' },
];


// ==============================================================================
// SETUP FLOW / UNIFIED (UAP) HELPERS
// ==============================================================================

// Platforms that still use the legacy (Advanced) flow by default. Everyone else defaults to Unified.
const LEGACY_DEFAULT_PLATFORMS = ['amazon', 'walmart'];

// Spend Tactic value that tells the backend to use the new sub-detail table
export const UNIFIED_SPEND_TACTIC = 'sub_detail';

/**
 * Default setup flow option for a NEW budget group on the given platform.
 * Amazon and Walmart default to Advanced Legacy, everything else to Unified.
 */
export const getDefaultSetupFlow = (platformKey) => (
  LEGACY_DEFAULT_PLATFORMS.includes(platformKey) ? SETUP_FLOW_ADVANCED : SETUP_FLOW_UNIFIED
);

/**
 * Setup flow options selectable on create.
 * - Amazon: Advanced Legacy + Unified (Unified only for 'item' budget type)
 * - Walmart: Advanced Legacy only (Unified coming later)
 * - Everyone else: Unified
 */
export const getSetupFlowOptions = (platformKey, budgetType) => {
  if (platformKey === 'amazon') {
    return budgetType === 'item' ? SETUP_FLOW_OPTIONS : [SETUP_FLOW_ADVANCED];
  }
  if (platformKey === 'walmart') {
    return [SETUP_FLOW_ADVANCED];
  }
  return [SETUP_FLOW_UNIFIED];
};

/**
 * Resolve the stored flow of an existing budget group into a flow option.
 * 'unified' stays unified. Legacy values ('quick', 'advanced') become Advanced Legacy.
 * A missing value on a platform that defaults to Unified is treated as Unified.
 */
export const resolveSetupFlow = (rawFlow, platformKey) => {
  if (rawFlow === 'unified') return SETUP_FLOW_UNIFIED;
  if (!rawFlow) return getDefaultSetupFlow(platformKey);
  return SETUP_FLOW_ADVANCED;
};

/** Value sent to the backend: only 'unified' or 'advanced'. */
export const normalizeSetupFlow = (value) => (value === 'unified' ? 'unified' : 'advanced');

/** True when the form is on Amazon with the Unified flow. */
export const isUnifiedAmazonFlow = (platformKey, setupFlow) => (
  platformKey === 'amazon' && setupFlow?.value === 'unified'
);

/**
 * The flow the legacy-gated child fields should see. Only Amazon + Unified changes the form;
 * on every other platform Unified is just a value passed to the backend, so the fields
 * keep rendering exactly as they did for 'advanced'.
 */
export const getEffectiveSetupFlow = (platformKey, setupFlow) => {
  if (setupFlow?.value === 'unified' && platformKey !== 'amazon') {
    return SETUP_FLOW_ADVANCED;
  }
  return setupFlow;
};

// Unified sub-types (Amazon). Each one has a checkbox and a daily Budget Amount.
const toSubDetailFieldId = (subType) => subType.replace(/[^A-Za-z0-9]+/g, '_');

export const UNIFIED_SUB_DETAIL_OPTIONS = [
  'DF_Brand_Search_SP_Keyword_Exact',
  'DF_Brand_Search_SP_Keyword_Phrase',
  'OF_Brand_Search_SP_Keyword_Custom',
  'OF_NonBrand_Search_SP_Keyword_Exact',
  'OF_NonBrand_Search_SP_Keyword_Phrase',
  'OF_NonBrand_Conquesting_SP_Category',
  'OF_NonBrand_Conquesting_SP_PDP_Items',
  'DF_Brand_Conquesting_SP_PDP_Items',
  'OF_NonBrand_Smart_Auto_SP',
].map(value => ({
  value,
  label: value.replace(/_/g, ' '),
  checkField: `unifiedSub_${toSubDetailFieldId(value)}`,
  amountField: `unifiedSubBudget_${toSubDetailFieldId(value)}`,
}));

const roundMoney = (value) => Math.round(value * 100) / 100;

/** Checked sub-types from the form, as [{ configuration_key, budget }]. Unchecked rows are omitted. */
export const buildUnifiedSubDetails = (formData) => UNIFIED_SUB_DETAIL_OPTIONS
  .filter(option => formData?.[option.checkField])
  .map(option => ({
    configuration_key: option.value,
    budget: parseFloat(formData[option.amountField]) || 0,
  }));

/**
 * Saved sub-detail rows -> form values (checkbox + amount per sub-type).
 * Only rows with bgd_spec_status 'ON' are checked. 'OFF' rows stay unchecked but still
 * get their saved amount (when set) so it is visible and not lost if re-checked.
 */
export const parseUnifiedSubDetails = (subDetails) => {
  const values = {};
  if (!Array.isArray(subDetails)) return values;
  subDetails.forEach((row) => {
    const option = UNIFIED_SUB_DETAIL_OPTIONS.find(opt => opt.value === row?.configuration_key);
    if (!option) return;
    if (row.bgd_spec_status === 'ON') {
      values[option.checkField] = true;
    }
    if (row.budget !== null && row.budget !== undefined && row.budget !== '') {
      values[option.amountField] = row.budget;
    }
  });
  return values;
};

/** Unified Daily Budget = sum of the checked sub-type amounts (single item, so no multiplier). */
export const computeUnifiedDailyBudget = (formData) => roundMoney(
  buildUnifiedSubDetails(formData).reduce((sum, row) => sum + row.budget, 0),
);

const parseItemCsv = (csv) => (csv || '').toString().split(',').map(item => item.trim()).filter(item => item);

/** Number of items in the ON list + OFF list combined. Unified requires exactly 1. */
export const countUnifiedItems = (onCsv, offCsv) => parseItemCsv(onCsv).length + parseItemCsv(offCsv).length;

/**
 * Form values forced for an Amazon Unified budget group (these fields are hidden in the UI).
 * @param {number} accountType - sellerSelect.type (1 = SKU, 2 = ASIN)
 */
export const getUnifiedAmazonDefaults = (accountType) => ({
  portfolioIdsCsv: '',
  includes: { label: 'Limitless ADs', value: 'limitless' },
  spendSpeed: { label: 'Standard', value: 'speed_level_1' },
  spendTactic: { label: 'Sub Detail', value: UNIFIED_SPEND_TACTIC },
  limitlessBudget: { label: 'Strict Budget', value: 0 },
  itemType: accountType === 1 ? { label: 'SKU', value: 'SKU' } : { label: 'ASIN', value: 'ASIN' },
  itemSelection: { label: 'All', value: 'ALL' },
  destination: { label: 'On', value: 'ON' },
  reallocation: 0,
  spendPriority: { label: 'Brand and NonBrand', value: '0' },
  costTargetType: { label: 'Brand/NonBrand', value: 'bnb' },
  budgetDriver: { label: 'Daily', value: 'daily' },
  // Unified allocation is always 100% SP
  budgetAllocationSpSd: 100,
  budgetAllocationSb: 0,
  budgetAllocationSt: 0,
  splitItemsCheckbox: false,
});

// Negative keywords are not captured for Unified
export const UNIFIED_CLEARED_FIELDS = {
  negativeKeywords: '',
  negativePhrase: false,
  negativeExact: false,
};
